import { Search, X } from 'lucide-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import { Input } from '~/components/ui/input';
import { Switch } from '~/components/ui/switch';
import { DIFF_STATUS_CFG, DIFF_STATUS_ORDER } from '~/constants/diff';
import type { DiffStatus } from '~/types/product-diff';
import { cn } from '~/utils/cn';

interface LegendProps {
  counts: Record<DiffStatus, number>;
  active: DiffStatus | null;
  onToggle: (status: DiffStatus) => void;
  /** `data-testid` root; derives `-{status}-button` and `-{status}-count`. */
  testId?: string;
}

/**
 * Colour key that doubles as a single-select row filter — the colours a reviewer
 * is already reading are the ones they can isolate, so "just show me what
 * changed" needs no extra control. Only statuses the data actually contains are
 * listed; a key entry for a colour that appears nowhere teaches nothing.
 */
export function DiffLegend({
  counts,
  active,
  onToggle,
  testId = 'attribute-diff-legend',
}: LegendProps) {
  const { t } = useTranslation('common');

  return (
    <div className="flex flex-wrap items-center gap-0.5" data-testid={testId}>
      {DIFF_STATUS_ORDER.filter((status) => counts[status] > 0).map((status) => {
        const cfg = DIFF_STATUS_CFG[status];
        const label = t(`productDetailsComparison.status.${cfg.labelKey}`);
        const isActive = active === status;
        return (
          <Button
            key={status}
            type="button"
            variant="ghost"
            size="sm"
            aria-pressed={isActive}
            data-testid={`${testId}-${status}-button`}
            title={
              isActive
                ? t('productDetailsComparison.statusFilterClear')
                : t('productDetailsComparison.statusFilterShow', { label: label.toLowerCase() })
            }
            onClick={(e) => {
              // The panels layout renders this strip inside a collapsible's
              // content; stopping propagation keeps a click off the host trigger.
              e.stopPropagation();
              onToggle(status);
            }}
            className={cn(
              // The selected outline is a *border*, not a ring: a ring paints
              // outside the box, so an ancestor with `overflow-hidden` shaves its
              // top edge off. Every chip carries a transparent one so selecting a
              // chip can't shift the strip by a pixel.
              'h-auto cursor-pointer gap-1.5 rounded-md border border-transparent px-2 py-1 text-xs font-normal',
              isActive
                ? 'bg-selected-row text-foreground border-primary/30 font-medium'
                : 'text-muted-foreground',
            )}
          >
            <span className={cn('h-2 w-2 shrink-0 rounded-full', cfg.dot)} aria-hidden="true" />
            {label}
            {/* Explicit space: JSX drops the newline, which would otherwise glue
                the count onto the label in the button's accessible name. */}{' '}
            <span
              className="text-muted-foreground tabular-nums"
              data-testid={`${testId}-${status}-count`}
            >
              {counts[status]}
            </span>
          </Button>
        );
      })}
    </div>
  );
}

interface SearchProps {
  value: string;
  onChange: (value: string) => void;
  /** `data-testid` for the input; derives `-clear-button`. */
  testId?: string;
}

/**
 * Attribute search. A local `type="search"` input rather than the shared
 * `SearchInput`: this one is debounced by the host and labelled for the
 * screen-reader-only label below, which that component doesn't expose.
 */
export function AttributeSearch({
  value,
  onChange,
  testId = 'attribute-search-input',
}: SearchProps) {
  const { t } = useTranslation('common');
  const placeholder = t('productDetailsComparison.searchPlaceholder');

  return (
    <div className="relative w-full sm:max-w-xs">
      <Search
        className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2"
        aria-hidden="true"
      />
      <Input
        type="search"
        aria-label={placeholder}
        data-testid={testId}
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        className={cn('h-8 pl-9 text-sm', value && 'pr-8')}
      />
      {value !== '' && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t('search')}
          data-testid={`${testId}-clear-button`}
          onClick={() => {
            onChange('');
          }}
          className="absolute top-1/2 right-1 h-6 w-6 -translate-y-1/2 cursor-pointer"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      )}
    </div>
  );
}

interface PairToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** `data-testid` for the checkbox. */
  testId?: string;
}

/** Pairing switch. Rendered only when the payload carries replacement links and
 *  the feature gate is on — a checkbox that cannot change a row is worse than no
 *  checkbox at all. */
export function PairReplacedToggle({
  checked,
  onChange,
  testId = 'pair-replaced-checkbox',
}: PairToggleProps) {
  const { t } = useTranslation('common');
  const id = 'pair-replaced-attributes';

  return (
    <div
      className="flex items-center gap-1.5"
      title={t('productDetailsComparison.pairReplacedHint')}
    >
      <Checkbox
        id={id}
        data-testid={testId}
        checked={checked}
        onCheckedChange={(next) => {
          onChange(next === true);
        }}
        className="cursor-pointer"
      />
      <label htmlFor={id} className="text-muted-foreground cursor-pointer text-[12.5px]">
        {t('productDetailsComparison.pairReplaced')}
      </label>
    </div>
  );
}

interface SyncScrollToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** `data-testid` for the switch. */
  testId?: string;
}

/** Links the two columns' vertical scroll. On by default so the Current and
 *  Lily AI values stay level; off lets a reviewer scroll one side on its own.
 *  A unique id keeps the label bound to the right switch when several
 *  comparisons render on one page. */
export function SyncScrollToggle({
  checked,
  onChange,
  testId = 'sync-scroll-toggle',
}: SyncScrollToggleProps) {
  const { t } = useTranslation('common');
  const id = useId();

  return (
    <div className="flex items-center gap-1.5" title={t('productDetailsComparison.syncScrollHint')}>
      <Switch id={id} data-testid={testId} checked={checked} onCheckedChange={onChange} />
      <label htmlFor={id} className="text-muted-foreground cursor-pointer text-[12.5px]">
        {t('productDetailsComparison.syncScroll')}
      </label>
    </div>
  );
}
